import React, { PropsWithChildren, useMemo, useState } from 'react';
import useTypedPage from '@/Hooks/useTypedPage';
import Modal from '../Modal';
import PlanCard from '../PlanCard';
import Button from '@/Components/Button';
import { cn } from '@/utils';
import IconShield from '@/svgs/shield.svg?react';
import IconDelete from '@/svgs/delete.svg?react';

type DurationTimeframes = 'monthly' | 'yearly';

export default function BillingModal({
  open,
  close,
  headline = "Advanced features and no hidden pricing. Try it out for free and upgrade when you're ready.",
}: {
  open: boolean;
  close: () => void;
  headline?: string;
}) {
  const { plans, auth, sunset } = useTypedPage().props;
  const team = auth?.user?.current_team;
  const [duration, setDuration] = useState<DurationTimeframes>('monthly');
  const plansOfInterval = useMemo(
    () =>
      plans
        .filter(plan => plan.interval === duration)
        .filter(plan => {
          // Filters out downgrades.
          if (
            (plan.slug === 'free' && team?.billing.plan !== 'free') ||
            (plan.slug === 'pro' && team?.billing.plan === 'agency')
          ) {
            return false;
          } else {
            return true;
          }
        }),
    [plans, duration]
  );

  function DurationToggleBtn({
    d,
    children,
  }: PropsWithChildren<{ d: DurationTimeframes }>) {
    return (
      <Button
        type="button"
        className={cn(
          'group rounded-xl px-4 py-1 text-sm',
          'text-brand-dark-100/90  hover:text-brand-light-100/80  dark:text-brand-light-25 hover:dark:text-brand-dark-100',
          'bg-brand-75 hover:bg-brand-dark-15 dark:bg-brand-dark-50',
          {
            'bg-brand-dark-15 text-brand-light-100/80 dark:bg-brand-dark-25':
              duration === d,
          }
        )}
        onClick={() => setDuration(d)}
      >
        {children}
      </Button>
    );
  }

  function Plans() {
    return (
      <div className="relative p-5 sm:px-8 sm:pb-6 sm:pt-8">
        <Button
          className="absolute right-2 top-1 bg-transparent p-1 hover:bg-brand-dark-20 dark:text-brand-light-25 hover:dark:text-brand-dark-100"
          onClick={close}
        >
          <IconDelete className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-center text-xl font-semibold dark:text-brand-light-100">
            Choose The Plan That Works For You
          </h1>
          <p className="pt-2 text-center text-lg">{headline}</p>
        </div>
        <div className="flex justify-center pb-5 pt-5 sm:pb-8 sm:pt-6">
          <div className="flex gap-1 rounded-xl border border-brand-dark-20 p-1">
            <DurationToggleBtn d="monthly">Monthly Billing</DurationToggleBtn>
            <DurationToggleBtn d="yearly">
              Yearly{' '}
              <span className="text-brand-light-100/80 dark:text-brand-dark group-hover:dark:text-brand-dark-100">
                (Save up to 33%)
              </span>
            </DurationToggleBtn>
          </div>
        </div>
        <div
          className={cn('grid grid-cols-1 gap-5', {
            'sm:grid-cols-3': plansOfInterval.length > 2,
            'sm:grid-cols-2': plansOfInterval.length < 3,
          })}
        >
          {plansOfInterval.map(plan => (
            <PlanCard
              active={plan.slug === team?.billing.plan}
              key={plan.id}
              plan={plan}
            />
          ))}
        </div>
        <span className="flex justify-center pt-4">
          <span className="flex items-center gap-2 text-center text-sm dark:text-brand-light-25">
            <IconShield /> Secure payment. Cancel anytime, 100% money back
            guarantee.
          </span>
        </span>
      </div>
    );
  }

  function Sunset() {
    return (
      <div className="relative p-5 sm:px-8 sm:pb-6 sm:pt-8">
        <Button
          className="absolute right-2 top-1 bg-transparent p-1 hover:bg-brand-dark-20 dark:text-brand-light-25 hover:dark:text-brand-dark-100"
          onClick={close}
        >
          <IconDelete className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-center text-xl font-semibold dark:text-brand-light-100">
            CodeWP Discontinued
          </h1>
          <p className="pt-2 text-center text-lg leading-normal">
            Due to recent developments, WPAI will be discontinuing our current
            product offerings, including CodeWP. We’re excited to provide
            additional information about these changes in the coming days
            <br />
            Thank you for your understanding and continued trust in our
            services.
            <a
              className="text-brand underline-offset-4 hover:underline"
              href="https://wpai.co/sunset"
            >
              Read more about it here
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <Modal isOpen={open} onClose={close} maxWidth={sunset ? 'xl' : '3xl'}>
      {sunset ? <Sunset /> : <Plans />}
    </Modal>
  );
}
